import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

const getToken = () => {
  try {
    const localData = localStorage.getItem("loggedUser");
    if (localData) {
      const parsed = JSON.parse(localData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }
    const sessionData = sessionStorage.getItem("loggedUser");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }
    return null;
  } catch (error) {
    return null;
  }
};

// ============================================================
// VARIANTS
// ============================================================

export const fetchVariants = createAsyncThunk(
  "variants/fetchVariants",
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/dashboard/variants`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const addVariant = createAsyncThunk(
  "variants/addVariant",
  async ({ name_ar, name_en, key }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("name[ar]", name_ar);
      formData.append("name[en]", name_en);
      formData.append("key", key);
      const response = await axios.post(
        `${BASE_URL}/dashboard/variants`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const updateVariant = createAsyncThunk(
  "variants/updateVariant",
  async ({ id, name_ar, name_en, key }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("name[ar]", name_ar);
      formData.append("name[en]", name_en);
      formData.append("key", key);
      const response = await axios.post(
        `${BASE_URL}/dashboard/variants/${id}/update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const deleteVariant = createAsyncThunk(
  "variants/deleteVariant",
  async (id, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      await axios.delete(`${BASE_URL}/dashboard/variants/${id}/destroy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// ============================================================
// VARIANT VALUES
// ============================================================

export const fetchVariantValues = createAsyncThunk(
  "variants/fetchVariantValues",
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/dashboard/variant-values`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const updateVariantValue = createAsyncThunk(
  "variants/updateVariantValue",
  async ({ id, name_ar, name_en, variant_id }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("name[ar]", name_ar);
      formData.append("name[en]", name_en);
      formData.append("variant_id", variant_id);
      const response = await axios.post(
        `${BASE_URL}/dashboard/variant-values/${id}/update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const deleteVariantValue = createAsyncThunk(
  "variants/deleteVariantValue",
  async (id, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      await axios.delete(`${BASE_URL}/dashboard/variant-values/${id}/destroy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const addVariantValue = createAsyncThunk(
  "variants/addVariantValue",
  async ({ name_ar, name_en, variant_id }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("name[ar]", name_ar);
      formData.append("name[en]", name_en);
      formData.append("variant_id", variant_id);
      const response = await axios.post(
        `${BASE_URL}/dashboard/variant-values`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// ============================================================
// SLICE
// ============================================================

const variantSlice = createSlice({
  name: "variants",
  initialState: {
    variants: [],
    variantValues: [],
    loading: false,
    actionLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearVariantState: (state) => {
      state.loading = false;
      state.actionLoading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Variants
      .addCase(fetchVariants.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchVariants.fulfilled, (state, action) => {
        state.loading = false;
        state.variants = action.payload.data || action.payload || [];
      })
      .addCase(fetchVariants.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Add Variant
      .addCase(addVariant.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(addVariant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload?.message || "تمت الإضافة بنجاح";
      })
      .addCase(addVariant.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })

      // Update Variant
      .addCase(updateVariant.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(updateVariant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload?.message || "تم التعديل بنجاح";
      })
      .addCase(updateVariant.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })

      // Delete Variant
      .addCase(deleteVariant.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(deleteVariant.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = "تم الحذف بنجاح";
        state.variants = state.variants.filter((v) => v.id !== action.payload.id);
      })
      .addCase(deleteVariant.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })

      // Fetch Variant Values
      .addCase(fetchVariantValues.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchVariantValues.fulfilled, (state, action) => {
        state.loading = false;
        state.variantValues = action.payload.data || action.payload || [];
      })
      .addCase(fetchVariantValues.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Update Variant Value
      .addCase(updateVariantValue.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(updateVariantValue.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload?.message || "تم التعديل بنجاح";
      })
      .addCase(updateVariantValue.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })

      // Delete Variant Value
      .addCase(deleteVariantValue.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(deleteVariantValue.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = "تم الحذف بنجاح";
        state.variantValues = state.variantValues.filter((v) => v.id !== action.payload.id);
      })
      .addCase(deleteVariantValue.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })

      // Add Variant Value
      .addCase(addVariantValue.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(addVariantValue.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload?.message || "تمت إضافة القيمة بنجاح";
      })
      .addCase(addVariantValue.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; });
  },
});

export const { clearVariantState } = variantSlice.actions;
export default variantSlice.reducer;
